// src/components/IDE/CodeRuntime/services/ideCodeInterpreter.ts
import * as acorn from 'acorn';
import * as walk from 'acorn-walk';

/**
 * Interface similaire au codeInterpreter de Blockly,
 * adaptée pour l'IDE (CodeMirror).
 */
export interface CodeInterpreter {
    init: (code: string) => void;
    step: () => Promise<void>;
    runAll: () => Promise<void>;
    hasMore: () => boolean;
    injectUserFunctionCall: (fnObject: UserFunction, additionalContext?: Record<string, any>) => void;
    setPaused: (paused: boolean) => void;
    setRunning: (running: boolean) => void;
    getVar: (name: string) => any;
    stop: () => void;
}

/** Représente une fonction utilisateur */
interface UserFunction {
    type: 'UserFunction';
    params: string[];
    body: any[];
    isArrow?: boolean;
}

/** Contexte d'exécution (injecté depuis l’extérieur) */
interface ExecutionContext {
    isRunning: boolean;
    isPaused: boolean;
    currentStep: number;
    currentNode?: any;
    ignoreBreakpoints: boolean;

    addLog: (msg: string) => void;
    stop: () => void;
    nextStep: () => void;
}

export function createIDECodeInterpreter(
    executionContext: ExecutionContext,
    onRegisterEvent?: (eventName: string, userFnAST: UserFunction, eventParams?: string[]) => void
): CodeInterpreter {
    // AST principal (le "main code")
    let mainAst: any = null;

    // Pile d'exécution
    let executionStack: any[] = [];

    // Variables "globales" visibles par le code utilisateur
    let contextVariables: Record<string, any> = {};

    // Délai step-by-step
    let delay = 300;

    // Dernière valeur de return
    let lastReturnValue: any = undefined;

    const eventHandlers: { [key: string]: Array<{ eventParams: string[]; fn: UserFunction }> } = {};

    function reorderCode(code: string): string {
        const ast = acorn.parse(code, { ecmaVersion: 'latest', sourceType: 'module', ranges: true }) as any;
        let hoistedParts = '';
        let otherParts = '';
        for (const node of ast.body) {
            if (node.type === 'FunctionDeclaration') {
                hoistedParts += code.slice(node.start, node.end) + '\n';
            } else {
                otherParts += code.slice(node.start, node.end) + '\n';
            }
        }
        return hoistedParts + '\n' + otherParts;
    }

    function init(code: string) {
        // Reset
        executionStack = [];
        contextVariables = {};
        lastReturnValue = undefined;

        // 1) Séparer les blocs events
        const { eventBlocks, mainCode } = separateEventBlocks(code);

        // 2) Enregistrer les events
        eventBlocks.forEach((eb) => registerEventBlock(eb));

        // 3) Parser le main code
        const reorderedMainCode = reorderCode(mainCode);
        try {
            mainAst = acorn.parse(reorderedMainCode, {
                ecmaVersion: 'latest',
                sourceType: 'module',
                allowAwaitOutsideFunction: true as any,
                locations: true,
            });
            // executionContext.addLog('Code principal analysé avec succès (IDE).');
        } catch (err: any) {
            executionContext.addLog(`Erreur parse code principal: ${err.message}`);
            executionContext.stop();
        }

        // 4) Injecter des globals **neutres** + ceux fournis par l’adapter (via __globals)
        const injectedGlobals = ((executionContext as any).__globals ?? {}) as Record<string, any>;
        contextVariables = {
            // builtins neutres
            window,
            console: {
                log: (...args: any[]) => {
                    const message = args.map(arg =>
                        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                    ).join(' ');
                    executionContext.addLog(message);
                    if ((executionContext as any).__logs) {
                        (executionContext as any).__logs.push(message);
                    }
                    // Optionnel: garder aussi dans la console navigateur
                    // window.console.log(...args);
                },
                info: (...args: any[]) => {
                    const message = args.map(arg =>
                        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                    ).join(' ');
                    executionContext.addLog(message);
                    if ((executionContext as any).__logs) {
                        (executionContext as any).__logs.push(message);
                    }
                    // window.console.info(...args);
                },
                warn: (...args: any[]) => {
                    const message = args.map(arg =>
                        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                    ).join(' ');
                    executionContext.addLog(`WARN: ${message}`);
                    if ((executionContext as any).__logs) {
                        (executionContext as any).__logs.push(`WARN: ${message}`);
                    }
                    // window.console.warn(...args);
                },
                error: (...args: any[]) => {
                    const message = args.map(arg =>
                        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                    ).join(' ');
                    executionContext.addLog(`ERROR: ${message}`);
                    if ((executionContext as any).__logs) {
                        (executionContext as any).__logs.push(`ERROR: ${message}`);
                    }
                    // window.console.error(...args);
                },
            },
            Math,

            // helpers utiles en pédagogie
            random: (min?: number, max?: number) => {
                // random() -> [0,1), random(max) -> [0,max), random(min,max) -> [min,max)
                if (min === undefined && max === undefined) return Math.random();
                if (max === undefined) return Math.random() * (min as number);
                return Math.random() * ((max as number) - (min as number)) + (min as number);
            },
            random_item: (arr: any[]) => (Array.isArray(arr) && arr.length ? arr[Math.floor(Math.random() * arr.length)] : undefined),

            // globals injectés par l’adapter (ex: { engine: bridge, say:..., move_to:... })
            ...injectedGlobals,
        };

        // utilitaire “math sur liste” (pédago)
        contextVariables['handleMathOnList'] = function (arr: any, operation: any) {
            if (!Array.isArray(arr)) throw new Error('handleMathOnList: le premier paramètre doit être un tableau');
            switch (operation) {
                case 'SUM': return arr.reduce((acc, x) => acc + x, 0);
                case 'MIN': return Math.min(...arr);
                case 'MAX': return Math.max(...arr);
                case 'AVERAGE': return arr.length ? arr.reduce((a, x) => a + x, 0) / arr.length : 0;
                case 'MEDIAN': {
                    if (!arr.length) return 0;
                    const s = [...arr].sort((a, b) => a - b);
                    const mid = Math.floor(s.length / 2);
                    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
                }
                case 'MODES': {
                    if (!arr.length) return [];
                    const m: Record<string, number> = {};
                    arr.forEach((x) => (m[x] = (m[x] || 0) + 1));
                    let max = 0;
                    Object.values(m).forEach((v) => (max = Math.max(max, v)));
                    return Object.keys(m).filter((k) => m[k] === max).map(Number);
                }
                case 'STD_DEV': {
                    if (arr.length <= 1) return 0;
                    const mean = arr.reduce((a, x) => a + x, 0) / arr.length;
                    const variance = arr.reduce((a, x) => a + (x - mean) ** 2, 0) / arr.length;
                    return Math.sqrt(variance);
                }
                case 'RANDOM_ITEM': return arr.length ? arr[Math.floor(Math.random() * arr.length)] : undefined;
                default: throw new Error('handleMathOnList: opération inconnue : ' + operation);
            }
        };
    }

    // Breakpoints injectés par l’IDE (via shim.ts)
    const getBreakpoints = () => ((executionContext as any).__getBreakpoints?.() as number[]) || [];
    const isIgnoringBreakpoints = () => !!(executionContext as any).__ignoreBreakpoints?.();

    function sleep(ms: number) { return new Promise(res => setTimeout(res, ms)); }

    function getVar(name: string) {
        return contextVariables[name];
    }

    function separateEventBlocks(fullCode: string): { eventBlocks: string[]; mainCode: string } {
        const lines = fullCode.split('\n');
        const eventBlocks: string[] = [];
        const mainLines: string[] = [];

        let i = 0;
        while (i < lines.length) {
            const line = lines[i];
            if (line.trim().startsWith('@on(')) {
                const blockLines: string[] = [];
                let braceCount = 0;
                do {
                    const l = lines[i];
                    blockLines.push(l);
                    braceCount += (l.match(/{/g) || []).length;
                    braceCount -= (l.match(/}/g) || []).length;
                    i++;
                } while (i < lines.length && braceCount > 0);
                eventBlocks.push(blockLines.join('\n'));
            } else {
                mainLines.push(line);
                i++;
            }
        }
        return { eventBlocks, mainCode: mainLines.join('\n') };
    }

    function registerEventBlock(eventBlockStr: string) {
        const firstLine = eventBlockStr.split('\n')[0];
        const match = firstLine.match(/^@on\((.*)\)\s*\{/);
        if (!match) {
            executionContext.addLog('Format @on(...) non reconnu : ' + firstLine);
            return;
        }
        const raw = match[1];
        const args = raw.split(',').map((s) => s.trim().replace(/^['"]|['"]$/g, ''));
        const eventName = args[0] || 'unknownEvent';
        const eventParams = args.slice(1);

        const idxFirstBrace = eventBlockStr.indexOf('{');
        const idxLastBrace = eventBlockStr.lastIndexOf('}');
        const bodyContent = eventBlockStr.substring(idxFirstBrace + 1, idxLastBrace).trim();
        const pseudoFn = `function __eventCallback(){\n${bodyContent}\n}`;
        let functionNode: any;
        try {
            const tmpAst = acorn.parse(pseudoFn, { ecmaVersion: 'latest', sourceType: 'module', locations: true });
            walk.simple(tmpAst, { FunctionDeclaration(node: any) { functionNode = node; } });
        } catch (err: any) {
            executionContext.addLog(`Erreur parse event block : ${err.message}`);
            return;
        }
        if (!functionNode) {
            executionContext.addLog('Pas de FunctionDeclaration trouvée dans le bloc @on.');
            return;
        }
        const userFnAST = compileUserFunction(functionNode);

        if (onRegisterEvent) {
            onRegisterEvent(eventName, userFnAST, eventParams);
            // executionContext.addLog(`Événement "${eventName}" enregistré (params=${JSON.stringify(eventParams)})`);
        }

        if (!eventHandlers[eventName]) eventHandlers[eventName] = [];
        eventHandlers[eventName].push({ eventParams, fn: userFnAST });
    }

    function hasMore(): boolean {
        return executionStack.length > 0 || mainAst !== null;
    }

    async function step(): Promise<void> {
        // si on est arrêté: ne rien faire
        if (!executionContext.isRunning) return;

        // plus rien à exécuter ? on reste "idle" (pas de stop auto)
        if (!hasMore()) return;

        if (executionStack.length === 0 && mainAst) {
            walk.simple(mainAst, {
                Program(node: any) {
                    for (let i = node.body.length - 1; i >= 0; i--) {
                        executionStack.push(node.body[i]);
                    }
                },
            });
            mainAst = null;
        }

        if (executionStack.length > 0) {
            const currentNode = executionStack.pop();
            executionContext.currentNode = currentNode;
            try {
                await executeStatement(currentNode);
                executionContext.nextStep();
            } catch (err: any) {
                executionContext.addLog(`Erreur exécution node ${currentNode?.type}: ${err.message}`);
                executionContext.stop();
                return;
            }

            if (executionStack.length > 0 && !isIgnoringBreakpoints()) {
                const nextNode = executionStack[executionStack.length - 1];
                const line = nextNode?.loc?.start?.line;
                if (typeof line === 'number' && getBreakpoints().includes(line)) {
                    // executionContext.addLog(`Breakpoint atteint à la ligne ${line}`);
                    executionContext.isPaused = true;
                }
            }
        }
    }

    async function runAll(): Promise<void> {
        // On laisse tourner tant que l’exécution n’est pas stoppée
        while (executionContext.isRunning) {
            if (executionContext.isPaused) {
                await sleep(50);
                continue;
            }
            if (hasMore()) {
                await step();
                await sleep(delay); // “vitesse” visible pour les pas
            } else {
                // Idle: pas d’instruction -> on attend un event qui injectera du code
                await sleep(50);
            }
        }
    }

    function setPaused(paused: boolean) { executionContext.isPaused = paused; }
    function setRunning(running: boolean) { executionContext.isRunning = running; }

    // ======================  Dispatch des statements  ======================
    async function executeStatement(node: any): Promise<void> {
        switch (node.type) {
            case 'VariableDeclaration': return handleVariableDeclaration(node);
            case 'ExpressionStatement': return evalExpression(node.expression).then(() => { });
            case 'ForStatement': return handleForStatement(node);
            case 'ForOfStatement': return handleForOfStatement(node);
            case 'WhileStatement': return handleWhileStatement(node);
            case 'DoWhileStatement': return handleDoWhileStatement(node);
            case 'IfStatement': return handleIfStatement(node);
            case 'SwitchStatement': return handleSwitchStatement(node);
            case 'BreakStatement': return handleBreakStatement(node);
            case 'ContinueStatement': return handleContinueStatement(node);
            case 'FunctionDeclaration': return handleFunctionDeclaration(node);
            case 'ReturnStatement': return handleReturnStatement(node);

            case 'ForLoop': return executeForLoop(node);
            case 'ForOfLoop': return executeForOfLoop(node);
            case 'WhileLoop': return executeWhileLoop(node);
            case 'DoWhileLoop': return executeDoWhileLoop(node);
            case 'EndOfFunction': return;

            default:
                executionContext.addLog(`Instruction non prise en charge : ${node.type}`);
        }
    }

    async function handleVariableDeclaration(node: any) {
        for (const decl of node.declarations) {
            const varName = decl.id.name;
            const varValue = decl.init ? await evalExpression(decl.init) : undefined;
            contextVariables[varName] = varValue;
        }
    }
    async function handleFunctionDeclaration(node: any) {
        const fnName = node.id.name;
        const userFn = compileUserFunction(node);
        contextVariables[fnName] = userFn;
    }
    async function handleReturnStatement(node: any) {
        lastReturnValue = node.argument ? await evalExpression(node.argument) : undefined;
        while (executionStack.length > 0) {
            const popped = executionStack.pop();
            if (popped.type === 'EndOfFunction') break;
        }
    }

    // ===========================  For/While  ===========================
    async function handleForStatement(node: any) {
        if (node.init) await executeStatement(node.init);
        executionStack.push({ type: 'ForLoop', test: node.test, update: node.update, body: node.body.body });
    }
    async function executeForLoop(forNode: any) {
        const testResult = await evalExpression(forNode.test);
        if (testResult) {
            executionStack.push(forNode);
            if (forNode.update) executionStack.push({ type: 'ExpressionStatement', expression: forNode.update });
            for (let i = forNode.body.length - 1; i >= 0; i--) executionStack.push(forNode.body[i]);
        }
    }
    async function handleForOfStatement(node: any) {
        const varName = node.left.declarations[0].id.name;
        const iterable = await evalExpression(node.right);
        executionStack.push({ type: 'ForOfLoop', variable: varName, iterable, index: 0, body: node.body.body });
    }
    async function executeForOfLoop(loopNode: any) {
        if (loopNode.index < loopNode.iterable.length) {
            contextVariables[loopNode.variable] = loopNode.iterable[loopNode.index];
            executionStack.push({ ...loopNode, index: loopNode.index + 1 });
            for (let i = loopNode.body.length - 1; i >= 0; i--) executionStack.push(loopNode.body[i]);
        }
    }
    async function handleWhileStatement(node: any) {
        executionStack.push({ type: 'WhileLoop', test: node.test, body: node.body.body });
    }
    async function handleDoWhileStatement(node: any) {
        const bodyStatements = node.body.type === 'BlockStatement' ? node.body.body : [node.body];
        executionStack.push({ type: 'DoWhileLoop', test: node.test, body: bodyStatements });
        for (let i = bodyStatements.length - 1; i >= 0; i--) executionStack.push(bodyStatements[i]);
    }
    async function executeWhileLoop(whileNode: any) {
        const conditionResult = await evalExpression(whileNode.test);
        if (conditionResult) {
            executionStack.push(whileNode);
            for (let i = whileNode.body.length - 1; i >= 0; i--) executionStack.push(whileNode.body[i]);
        }
    }
    async function executeDoWhileLoop(doWhileNode: any) {
        const conditionResult = await evalExpression(doWhileNode.test);
        if (conditionResult) {
            executionStack.push(doWhileNode);
            for (let i = doWhileNode.body.length - 1; i >= 0; i--) executionStack.push(doWhileNode.body[i]);
        }
    }

    // =====================  if / switch / break / continue  =====================
    async function handleIfStatement(node: any) {
        const testResult = await evalExpression(node.test);
        const pushBody = (b: any[]) => { for (let i = b.length - 1; i >= 0; i--) executionStack.push(b[i]); };
        if (testResult) {
            if (node.consequent?.body) pushBody(node.consequent.body);
        } else if (node.alternate) {
            if (node.alternate.type === 'BlockStatement') pushBody(node.alternate.body);
            else if (node.alternate.type === 'IfStatement') executionStack.push(node.alternate);
        }
    }
    async function handleSwitchStatement(node: any) {
        const switchVal = await evalExpression(node.discriminant);
        let matchedCase = false;
        for (let i = 0; i < node.cases.length; i++) {
            const c = node.cases[i];
            if (c.test === null) {
                if (!matchedCase) for (let j = c.consequent.length - 1; j >= 0; j--) executionStack.push(c.consequent[j]);
                break;
            } else {
                const caseVal = await evalExpression(c.test);
                if (caseVal === switchVal) {
                    matchedCase = true;
                    for (let j = c.consequent.length - 1; j >= 0; j--) executionStack.push(c.consequent[j]);
                    break;
                }
            }
        }
    }
    async function handleBreakStatement(_node: any) {
        while (executionStack.length > 0) {
            const top = executionStack.pop();
            if (['ForLoop', 'ForOfLoop', 'WhileLoop'].includes(top.type)) break;
        }
    }
    async function handleContinueStatement(_node: any) {
        let foundLoop = false;
        while (executionStack.length > 0) {
            const top = executionStack.pop();
            if (['ForLoop', 'ForOfLoop', 'WhileLoop'].includes(top.type)) {
                if (top.type === 'ForLoop' && top.update) {
                    executionStack.push({ type: 'ExpressionStatement', expression: top.update });
                }
                executionStack.push(top);
                foundLoop = true;
                break;
            }
        }
        if (!foundLoop) throw new Error('"continue" utilisé en dehors d\'une boucle.');
    }

    // ===========================   Expressions   ===========================
    async function evalExpression(expr: any): Promise<any> {
        switch (expr.type) {
            case 'Literal': return expr.value;
            case 'Identifier': return evalIdentifier(expr);
            case 'AssignmentExpression': return evalAssignmentExpression(expr);
            case 'BinaryExpression': return evalBinaryExpression(expr);
            case 'UpdateExpression': return evalUpdateExpression(expr);
            case 'CallExpression': return evalCallExpression(expr);
            case 'MemberExpression': return evalMemberExpression(expr);
            case 'LogicalExpression': return evalLogicalExpression(expr);
            case 'UnaryExpression': return evalUnaryExpression(expr);
            case 'AwaitExpression': return handleAwaitExpression(expr);
            case 'ArrayExpression': return evalArrayExpression(expr);
            case 'FunctionExpression':
            case 'ArrowFunctionExpression': return compileUserFunction(expr);
            default: throw new Error(`Expression non supportée: ${expr.type}`);
        }
    }

    function evalIdentifier(expr: any): any {
        // 1) Priorité au contexte de l’interpréteur (inclut le console injecté)
        if (expr.name in contextVariables) {
            return contextVariables[expr.name];
        }

        // 2) Fallbacks contrôlés
        if (expr.name === 'window') return window;

        // 3) Dernier recours : globals du window
        if ((window as any)[expr.name] !== undefined) return (window as any)[expr.name];

        throw new Error(`Variable non déclarée: ${expr.name}`);
    }


    async function evalAssignmentExpression(expr: any): Promise<any> {
        const rightValue = await evalExpression(expr.right);
        if (expr.left.type === 'Identifier') {
            const varName = expr.left.name;
            switch (expr.operator) {
                case '=': contextVariables[varName] = rightValue; return contextVariables[varName];
                case '+=': contextVariables[varName] = (contextVariables[varName] || 0) + rightValue; return contextVariables[varName];
                case '-=': contextVariables[varName] = (contextVariables[varName] || 0) - rightValue; return contextVariables[varName];
                default: throw new Error(`Opérateur d'affectation non géré: ${expr.operator}`);
            }
        } else if (expr.left.type === 'MemberExpression') {
            const obj = await evalExpression(expr.left.object);
            const propName = expr.left.computed ? await evalExpression(expr.left.property) : expr.left.property.name;
            switch (expr.operator) {
                case '=': obj[propName] = rightValue; break;
                case '+=': obj[propName] = (obj[propName] || 0) + rightValue; break;
                case '-=': obj[propName] = (obj[propName] || 0) - rightValue; break;
                default: throw new Error(`Opérateur d'affectation non géré : ${expr.operator}`);
            }
            return obj[propName];
        } else {
            throw new Error("Affectation non gérée si la gauche n'est pas un Identifiant ou MemberExpression");
        }
    }

    async function evalBinaryExpression(expr: any): Promise<any> {
        const left = await evalExpression(expr.left);
        const right = await evalExpression(expr.right);
        switch (expr.operator) {
            case '+': return left + right;
            case '-': return left - right;
            case '*': return left * right;
            case '/': return left / right;
            case '%': return left % right;
            case '<': return left < right;
            case '<=': return left <= right;
            case '>': return left > right;
            case '>=': return left >= right;
            case '==': return left === right;
            case '!=': return left !== right;
            case '===': return left === right;
            case '!==': return left !== right;
            default: throw new Error(`Opérateur binaire non supporté : ${expr.operator}`);
        }
    }

    async function evalUpdateExpression(expr: any): Promise<any> {
        const varName = expr.argument.name;
        if (!(varName in contextVariables)) throw new Error(`Variable non déclarée : ${varName}`);
        if (expr.operator === '++') { contextVariables[varName]++; return contextVariables[varName]; }
        if (expr.operator === '--') { contextVariables[varName]--; return contextVariables[varName]; }
        throw new Error(`Opérateur update non supporté : ${expr.operator}`);
    }

    async function evalCallExpression(expr: any): Promise<any> {
        const calleeVal = await evalExpression(expr.callee);
        const args: any[] = [];
        for (const arg of expr.arguments) args.push(await evalExpression(arg));

        if (calleeVal && (calleeVal as any).type === 'UserFunction') {
            callUserFunction(calleeVal, args);
            while (executionStack.some((n) => n.type !== 'EndOfFunction')) {
                await step();
            }
            return lastReturnValue;
        } else if (typeof calleeVal === 'function') {
            return (calleeVal as Function)(...args);
        } else {
            throw new Error(`Cible d'appel invalide : ${expr.callee.type}`);
        }
    }

    async function evalMemberExpression(expr: any): Promise<any> {
        const objVal = await evalExpression(expr.object);
        const propName = expr.computed ? await evalExpression(expr.property) : expr.property.name;
        if (!objVal) throw new Error('Objet de MemberExpression est falsy');
        const propValue = objVal[propName];
        if (typeof propValue === 'function') return propValue.bind(objVal);
        return propValue;
    }

    async function evalLogicalExpression(expr: any): Promise<any> {
        const leftVal = await evalExpression(expr.left);
        switch (expr.operator) {
            case '&&': return leftVal && (await evalExpression(expr.right));
            case '||': return leftVal || (await evalExpression(expr.right));
            default: throw new Error(`Opérateur logique non supporté : ${expr.operator}`);
        }
    }

    async function evalUnaryExpression(expr: any): Promise<any> {
        const argument = await evalExpression(expr.argument);
        switch (expr.operator) {
            case '-': return -argument;
            case '+': return +argument;
            case '!': return !argument;
            case '~': return ~argument;
            case 'typeof': return typeof argument;
            case 'void': return void argument;
            default: throw new Error(`Opérateur unaire non supporté : ${expr.operator}`);
        }
    }

    async function handleAwaitExpression(expr: any): Promise<any> {
        const awaitedValue = await evalExpression(expr.argument);
        return awaitedValue;
    }

    async function evalArrayExpression(expr: any): Promise<any[]> {
        const arr: any[] = [];
        for (const el of expr.elements) arr.push(el === null ? undefined : await evalExpression(el));
        return arr;
    }

    // ======================   UserFunction management   ======================
    function compileUserFunction(fnAst: any): UserFunction {
        const params = fnAst.params.map((p: any) => p.name || 'arg');
        let body: any[] = [];
        if (fnAst.body.type === 'BlockStatement') body = fnAst.body.body;
        else body = [{ type: 'ReturnStatement', argument: fnAst.body }];
        return { type: 'UserFunction', params, body, isArrow: fnAst.type === 'ArrowFunctionExpression' };
    }

    function callUserFunction(fn: UserFunction, args: any[]) {
        lastReturnValue = undefined;
        fn.params.forEach((p, idx) => { contextVariables[p] = args[idx]; });
        for (let i = fn.body.length - 1; i >= 0; i--) executionStack.push(fn.body[i]);
        executionStack.push({ type: 'EndOfFunction' });
    }

    function injectUserFunctionCall(fn: UserFunction, additionalContext?: Record<string, any>) {
        if (additionalContext) Object.entries(additionalContext).forEach(([k, v]) => (contextVariables[k] = v));
        for (let i = fn.body.length - 1; i >= 0; i--) executionStack.push(fn.body[i]);
    }

    function stop() {
        executionContext.isRunning = false;
        executionContext.isPaused = false;
        executionStack = [];
        mainAst = null;
        // lastReturnValue éventuellement à reset si tu veux :
        // lastReturnValue = undefined;
    }

    return { init, step, runAll, hasMore, injectUserFunctionCall, setPaused, setRunning, getVar, stop };
}
